import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface AudioPlayerProps {
  file?: File | null;
  audioUrl?: string | null;
  fileName?: string;
  onDurationCalculated?: (duration: string) => void;
  className?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ 
  file, 
  audioUrl,
  fileName,
  onDurationCalculated, 
  className = "" 
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);

  useEffect(() => {
    if (file) {
      // Create audio URL from file
      const url = URL.createObjectURL(file);
      setCurrentAudioUrl(url);
      setFileSize(file.size);
      setError(null);

      // Cleanup function
      return () => {
        URL.revokeObjectURL(url);
        setCurrentAudioUrl(null);
      };
    } else if (audioUrl) {
      // Use provided URL
      setCurrentAudioUrl(audioUrl);
      setFileSize(null);
      setError(null);
    } else {
      setCurrentAudioUrl(null);
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
      setFileSize(null);
    }
  }, [file, audioUrl]);

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;
      if (!isNaN(audioDuration) && audioDuration > 0) {
        setDuration(audioDuration);
        
        // Format duration and call callback
        const minutes = Math.floor(audioDuration / 60);
        const seconds = Math.floor(audioDuration % 60);
        const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        
        if (onDurationCalculated) {
          onDurationCalculated(formattedDuration);
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch((err) => {
          console.error('Audio play error:', err);
          setError(t('audio.playError'));
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const seekTime = parseFloat(e.target.value);
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const formatTime = (time: number): string => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getFileSize = (sizeInBytes: number): string => {
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return `${sizeInMB.toFixed(1)} MB`;
  };

  const handleError = () => {
    setError(t('audio.loadError'));
    setIsPlaying(false);
  };

  const displayFileName = file ? file.name : (fileName || 'Audio File');

  if (!file && !audioUrl) {
    return (
      <div className={`bg-white/5 border-2 border-dashed border-white/30 rounded-xl p-6 text-center ${className}`}>
        <svg className="mx-auto h-12 w-12 text-white/40 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
        </svg>
        <p className="text-white/60 text-sm">{t('audio.noFileSelected')}</p>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-xl p-4 ${className}`}>
      {/* Audio element */}
      {currentAudioUrl && (
        <audio
          ref={audioRef}
          src={currentAudioUrl}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setIsPlaying(false)}
          onError={handleError}
          preload="metadata"
        />
      )}

      {/* File Info */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-blue-400 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
            </svg>
          </div>
          <div>
            <h4 className="text-white font-medium text-sm truncate max-w-[200px]" title={displayFileName}>
              {displayFileName}
            </h4>
            <p className="text-white/60 text-xs">
              {fileSize ? getFileSize(fileSize) + ' • ' : ''}{duration > 0 ? formatTime(duration) : t('audio.calculating')}
            </p>
          </div>
        </div>
        
        {/* File Type Badge */}
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
          WAV
        </span>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-3 p-2 bg-red-500/20 border border-red-500/30 rounded-lg">
          <p className="text-red-300 text-xs">{error}</p>
        </div>
      )}

      {/* Audio Controls */}
      <div className="space-y-3">
        {/* Play/Pause and Time */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePlayPause}
            disabled={!currentAudioUrl || error !== null}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
              !currentAudioUrl || error
                ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                : 'bg-purple-500 hover:bg-purple-600 text-white hover:scale-105 shadow-lg'
            }`}
          >
            {isPlaying ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <div className="text-xs text-white/70 font-mono">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <input
            type="range"
            min={0}
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            disabled={!currentAudioUrl || error !== null}
            className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer audio-slider"
            style={{
              background: `linear-gradient(to right, rgb(168 85 247) 0%, rgb(168 85 247) ${
                duration > 0 ? (currentTime / duration) * 100 : 0
              }%, rgb(255 255 255 / 0.1) ${
                duration > 0 ? (currentTime / duration) * 100 : 0
              }%, rgb(255 255 255 / 0.1) 100%)`
            }}
          />
        </div>
      </div>


    </div>
  );
};

export default AudioPlayer; 